import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { Select } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserSectionStats } from '../../../../user-section/models/user-section.model';
import { UserSectionsStatsState } from '../../../../user-section/models/user-sections.state';
import { UserSectionService } from '../../../../user-section/services/user-section.service';
import { ListParticipantsProgress } from './participants-progress.interface';

/**
 * Data source for the ParticipantsProgressDatasource view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class ParticipantsProgressDatasource extends DataSource<ListParticipantsProgress> {
    @Select(UserSectionsStatsState.getFilteredEntities)
    private _filteredUserSectionStat$: Observable<UserSectionStats[]>;

    @Select(UserSectionsStatsState.getPageSize)
    pageSize$: Observable<number>;

    @Select(UserSectionsStatsState.getTotalCount)
    totalCount$: Observable<number>;

    @Select(UserSectionsStatsState.getPageIndex)
    pageIndex$: Observable<number>;

    private _subscribeTotal: Subscription;

    filteredUserSectionStats: UserSectionStats[];

    paginator: MatPaginator | undefined;

    showSpinner = false;
    callBackApiFetch: any;
    totalRows = 0;

    private _unsubscribeAll: Subject<any>;

    constructor(private _userSectionService: UserSectionService) {
        super();

        this._unsubscribeAll = new Subject();

        this._subscribeTotal = this._filteredUserSectionStat$.pipe(takeUntil(this._unsubscribeAll)).subscribe((rows) => {
            if (rows) {
                this.totalRows = rows.length;
            } else {
                this.totalRows = 0;
            }
        });

        this.totalCount$.pipe(takeUntil(this._unsubscribeAll)).subscribe((totalCount) => {
            if (totalCount) {
                this.totalRows = totalCount;
            }
        });

        this.pageSize$.pipe(takeUntil(this._unsubscribeAll)).subscribe((lastPageSize) => {
            if (lastPageSize) {
                this.paginator.pageSize = lastPageSize;
            }
        });

        this.pageIndex$.pipe(takeUntil(this._unsubscribeAll)).subscribe((nbrIndex) => {
            if (nbrIndex) {
                this.paginator.pageIndex = nbrIndex;
            }
        });
    }

    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */
    connect(): Observable<any[]> {
        if (this.paginator) {
            return this._filteredUserSectionStat$;
        } else {
            throw Error('Please set the paginator and sort on the data source before connecting.');
        }
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */
    disconnect(): void {
        this._subscribeTotal.unsubscribe();
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    fetchUserSectionsStatsState(moduleId: string, pageSize: number, pageNumber: number, search?: string, status?: string): void {
        this.showSpinner = true;

        this._userSectionService.resetUserSectionStats();
        this._userSectionService.fetchUserSectionStats(moduleId, pageSize, pageNumber, '', '', search, status).then(
            () => {
                this.showSpinner = false;
            },
            (error) => {
                console.error(error);
                this.showSpinner = false;
            }
        );
    }
}
